/**
 * Services Management Page
 * - Lists all veterinary services/products
 * - Supports add, edit, and delete operations
 * - Displays service details including images, status, and descriptions
 */

import React, { useEffect, useState } from "react";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchServices, deleteService } from "../../redux/slices/servicesSlice";
import TableComponent from "../../components/common/TableComponent";
import DeleteModal from "../../components/common/DeleteModal";
import ImageModal from "../../components/common/ImageModal";
import { toast } from "react-toastify";

const statusClasses = {
  true: "bg-green-100 text-green-800",
  false: "bg-red-100 text-red-800",
  Active: "bg-green-100 text-green-800",
  Inactive: "bg-red-100 text-red-800",
  Default: "bg-gray-100 text-gray-600",
};

export default function Services() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const services = useSelector((state) => state.services.items);
  const status = useSelector((state) => state.services.status);

  const [selectedImage, setSelectedImage] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchServices());
  }, [dispatch]);

  const columns = [
    {
      key: "product_image_url",
      label: "Image",
      render: (val) => (
        <div className="w-12 h-12">
          {val ? (
            <img
              src={val}
              alt="Service"
              className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(val);
              }}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500">
              No Image
            </div>
          )}
        </div>
      ),
    },
    { key: "name", label: "Name" },
    { key: "title", label: "Title" },
      {
        key: "description",
        label: "Description",
        render: (val) => (
          <div className="max-w-[120px] sm:max-w-xs truncate text-xs sm:text-sm" title={val}>
            {val}
          </div>
        ),
      },
    {
      key: "active",
      label: "Status",
      render: (val) => (
        <span
          className={`px-3 py-1 rounded-md text-xs font-semibold ${
            statusClasses[val] || statusClasses.Default
          }`}
        >
          {val ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (val, row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/swift-app/admin/services/${row.uuid}/edit`);
            }}
            className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Edit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setServiceToDelete(row);
              setDeleteModalOpen(true);
            }}
            className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const handleRowClick = (row) => {
    navigate(`/swift-app/admin/services/${row.uuid}`);
  };

  const handleAddService = () => {
    navigate("/swift-app/admin/services/new");
  };

  const handleDeleteConfirm = async () => {
    if (serviceToDelete) {
      try {
        await dispatch(deleteService(serviceToDelete.uuid)).unwrap();
        toast.success("Service deleted successfully");
        setDeleteModalOpen(false);
        setServiceToDelete(null);
      } catch (error) {
        toast.error("Failed to delete service");
        console.error("Delete error:", error);
      }
    }
  };

  return (
    <div className="px-3 sm:px-6 md:px-8 lg:px-12 pt-6 md:pt-8 font-inter min-h-screen bg-white">
      {/* ✅ Header */}
      <div className="flex flex-col gap-4">
        {/* Top Row — Back button + Title + Add Button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              aria-label="Go Back"
              className="p-2 md:p-3 text-lg md:text-xl text-gray-800 hover:text-blue-600"
            >
              <MdOutlineArrowBackIos />
            </button>
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
              Services Management
            </h1>
          </div>

          {/* ✅ Add Service button */}
          <button
            onClick={handleAddService}
            className="flex items-center justify-center gap-2 bg-blue-500 text-white font-medium px-3 sm:px-4 md:px-5 py-2 rounded-lg shadow hover:bg-blue-600 transition"
          >
            <FiPlus className="w-5 h-5" />
            <span className="hidden sm:inline">Add Service</span>
          </button>
        </div>

        {/* Subtitle */}
        <p className="text-gray-600 text-sm sm:text-base ml-10 md:ml-14">
          Manage veterinary services and products
        </p>
      </div>

      {/* ✅ Table */}
      <div className="mt-8 md:mt-10 overflow-x-auto">
        {status === "loading" ? (
          <div className="text-center text-gray-500 py-10 text-sm md:text-base">
            Loading services...
          </div>
        ) : services.length === 0 ? (
          <div className="text-center text-gray-500 py-10 text-sm md:text-base">
            No services found. Click "Add Service" to create your first service.
          </div>
        ) : (
          <TableComponent
            columns={columns}
            data={services}
            onRowClick={handleRowClick}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setServiceToDelete(null);
        }}
        onDelete={handleDeleteConfirm}
        profileType={`${serviceToDelete?.name || "Service"}`}
      />

      {/* Image Modal */}
      <ImageModal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageSrc={selectedImage}
        altText="Service image"
      />
    </div>
  );
}
